import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { CouponInfo } from '../../../models';
import { fromCoupon, useAppDispatch } from '../../../store';

export interface CouponSectionProps {
  /** Callback when the user clicks "Apply" with a coupon code */
  onApply: (couponName: string) => void;
  /** Callback when the user dismisses an applied coupon */
  onRemove: () => void;
  /** Whether a preview request is in-flight */
  isLoading: boolean;
  /** The coupon currently applied (from the preview response), or null */
  appliedCoupon: CouponInfo | undefined;
  /** Error message to display when coupon validation fails, or null */
  error: string | null;
  /** Current plan interval is annual * */
  isAnnual: boolean;
}

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    marginBottom: theme.spacing(0.75),
  },
  inputRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'stretch',
  },
  textField: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      height: 40,
      fontSize: 14,
      borderRadius: 6,
      '& fieldset': {
        borderColor: '#D5D8DD',
      },
      '&:hover fieldset': {
        borderColor: '#B0B3BA',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#5959EB',
      },
    },
  },
  textFieldError: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#d24720',
      },
      '&:hover fieldset': {
        borderColor: '#d24720',
      },
    },
  },
  applyButton: {
    height: 40,
    minWidth: 80,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    border: '2px solid #5959EB',
    color: '#5959EB',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: '#F3F1FF',
    },
    '&.Mui-disabled': {
      borderColor: '#D5D8DD',
      color: '#B0B3BA',
    },
  },
  successAlert: {
    marginTop: theme.spacing(1),
    borderRadius: 6,
    backgroundColor: '#F0FFF4',
    color: '#0f8461',
    fontSize: 13,
    fontWeight: 500,
    '& .MuiAlert-icon': {
      color: '#0f8461',
    },
  },
  errorAlert: {
    marginTop: theme.spacing(1),
    borderRadius: 6,
    backgroundColor: '#FFF5F5',
    color: '#d24720',
    fontSize: 13,
    fontWeight: 500,
    '& .MuiAlert-icon': {
      color: '#d24720',
    },
  },
  dismissButton: {
    padding: 4,
  },
  dismissIcon: {
    fontSize: 14,
    color: 'rgba(104, 109, 128, 0.54)',
  },
}));

const CouponSection: React.FC<CouponSectionProps> = ({
  onApply,
  onRemove,
  isLoading,
  appliedCoupon,
  error,
  isAnnual,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState('');
  const couponInfo = useSelector(fromCoupon.selectCoupon);
  const couponRule = couponInfo?.rule;

  const handleApply = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      onApply(trimmed);
      dispatch(fromCoupon.doFetchCouponByCode(trimmed));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  const handleRemove = () => {
    setInputValue('');
    onRemove();
  };

  const formatDiscount = (coupon: CouponInfo): string => {
    if (coupon.discountType === 'PERCENT') {
      return `${coupon.discountAmount}%`;
    }
    return `$${coupon.appliedAmount}`;
  };

  const isApplied = appliedCoupon != null;

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>Coupon Code</Typography>

      {!isApplied && (
        <Box className={classes.inputRow}>
          <TextField
            className={`${classes.textField} ${error ? classes.textFieldError : ''}`}
            variant="outlined"
            placeholder="Enter coupon code"
            size="small"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <Button
            className={classes.applyButton}
            variant="outlined"
            onClick={handleApply}
            disabled={isLoading || !inputValue.trim()}
          >
            {isLoading
              ? (<FormattedMessage id="order.confirmation.coupon.validating" />)
              : (<FormattedMessage id="order.confirmation.coupon.apply" />)}
          </Button>
        </Box>
      )}

      {error && !isApplied && (
        <Alert
          className={classes.errorAlert}
          severity="error"
          icon={(
            <ErrorIcon style={{ fontSize: 16, color: '#d24720' }} />
          )}
        >
          <FormattedMessage
            id={error}
            defaultMessage="coupon.code.invalid"
            values={{
              quota: couponRule?.minLicenses,
              amount: couponRule?.minAmount,
              cycle: isAnnual ? 'monthly' : 'annual',
            }}
          />
        </Alert>
      )}

      {isApplied && (
        <Alert
          className={classes.successAlert}
          severity="success"
          icon={(
            <CheckCircleIcon style={{ fontSize: 16, color: '#0f8461' }} />
          )}
          action={(
            <IconButton
              className={classes.dismissButton}
              size="small"
              onClick={handleRemove}
            >
              <CancelIcon className={classes.dismissIcon} />
            </IconButton>
          )}
        >
          <FormattedMessage
            id="order.confirmation.coupon.applied"
            values={{
              code: appliedCoupon?.code,
              discount: formatDiscount(appliedCoupon),
            }}
          />
        </Alert>
      )}
    </Box>
  );
};

export default CouponSection;
